import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BetaLockOverlay } from '../../components/ui/BetaLockOverlay';
import { 
    Trophy, Star, TrendingUp, Target, Award, Users, BookOpen,
    ChevronRight, Calendar, Clock, CheckCircle, X, ArrowRight,
    BarChart3, Zap, Medal, Crown, Shield, Lock
} from 'lucide-react';
import api from '../../lib/api';
import { 
    LevelBadge, ConfidenceIndex, Badge, EloDisplay, 
    MissionCard, CertificationCard, SeasonProgress 
} from '../../components/reputation/ReputationComponents';

const AgentReputationPage = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [badges, setBadges] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [missions, setMissions] = useState({ weekly: [], monthly: [] });
    const [seasonData, setSeasonData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeCertification, setActiveCertification] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [statsRes, badgesRes, certsRes, weeklyRes, monthlyRes, seasonRes] = await Promise.all([
                api.get('/reputation/agent/current/stats'),
                api.get('/reputation/agent/current/badges'),
                api.get('/reputation/certifications'),
                api.get('/reputation/missions/weekly'),
                api.get('/reputation/missions/monthly'),
                api.get('/reputation/season/current')
            ]);

            if (statsRes.data) {
                setStats(statsRes.data);
            }

            if (badgesRes.data?.badges) {
                setBadges(badgesRes.data.badges);
            } else {
                setBadges([]);
            }

            setCertifications(certsRes.data?.certifications || []);
            
            setMissions({
                weekly: weeklyRes.data?.missions?.map(m => ({ 
                    ...m, 
                    progress: m.progress || 0 
                })) || [],
                monthly: monthlyRes.data?.missions?.map(m => ({ 
                    ...m, 
                    progress: m.progress || 0 
                })) || []
            });
            
            if (seasonRes.data) {
                const xp = statsRes.data?.season_xp || 0;
                const levels = seasonRes.data.xp_levels || [];
                let currentLevel = 1;
                let levelName = 'Débutant';
                let xpToNext = 100;
                
                for (let i = levels.length - 1; i >= 0; i--) {
                    if (xp >= levels[i].xp_required) {
                        currentLevel = levels[i].level;
                        levelName = levels[i].name;
                        xpToNext = i < levels.length - 1 ? levels[i + 1].xp_required - xp : 0;
                        break;
                    }
                }
                
                setSeasonData({
                    ...seasonRes.data,
                    season_xp: xp,
                    season_level: currentLevel,
                    level_name: levelName,
                    xp_to_next_level: xpToNext
                });
            }
        } catch (error) {
            console.error('Error loading reputation data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const tabs = [
        { id: 'overview', label: 'Résumé', shortLabel: 'Résumé', icon: BarChart3 },
        { id: 'badges', label: 'Badges', shortLabel: 'Badges', icon: Award },
        { id: 'certifications', label: 'Certifs', shortLabel: 'Certifs', icon: BookOpen, locked: true },
        { id: 'missions', label: 'Missions', shortLabel: 'Missions', icon: Target, locked: true },
        { id: 'ranking', label: 'Classement', shortLabel: 'Rang', icon: Trophy, locked: true }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="bg-background text-foreground" data-testid="reputation-page">
            {/* Header - Compact on mobile */}
            <div className="bg-card/50 border-b border-border">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
                    {/* Mobile: Compact layout */}
                    <div className="flex flex-col gap-4">
                        {/* Mobile top row: Level + Elo summary */}
                        <div className="flex items-center gap-3 sm:gap-6">
                            <LevelBadge level={stats?.level} size="lg" showProgress />
                            
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 sm:gap-6">
                                    <EloDisplay 
                                        elo={stats?.elo_season} 
                                        trend={stats?.trend}
                                        label="Saison"
                                        size="md"
                                    />
                                    <div className="w-px h-8 bg-gray-700 hidden sm:block"></div>
                                    <EloDisplay 
                                        elo={stats?.elo_career} 
                                        label="Carrière"
                                        size="md"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Mobile compact stats row */}
                        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 sm:hidden">
                            <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2.5 py-1.5 shrink-0">
                                <Shield size={14} className="text-blue-400" />
                                <span className="text-xs text-white font-medium">{stats?.confidence_index?.index || 0}%</span>
                                <span className="text-[10px] text-muted-foreground">IndiceHomly</span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2.5 py-1.5 shrink-0">
                                <Trophy size={14} className="text-yellow-400" />
                                <span className="text-xs text-white font-medium">#{stats?.rank_agency || '-'}</span>
                                <span className="text-[10px] text-muted-foreground">agence</span>
                            </div>
                            <div className="flex items-center gap-1.5 bg-white/5 rounded-lg px-2.5 py-1.5 shrink-0">
                                <Medal size={14} className="text-purple-400" />
                                <span className="text-xs text-white font-medium">#{stats?.rank_local || '-'}</span>
                                <span className="text-[10px] text-muted-foreground">secteur</span>
                            </div>
                        </div>
                        
                        {/* Desktop: Full layout */}
                        <div className="hidden sm:flex items-center gap-6 justify-end">
                            <ConfidenceIndex index={stats?.confidence_index} size="md" />
                            <div className="text-center">
                                <div className="text-sm text-muted-foreground mb-1">Classements</div>
                                <div className="flex gap-4">
                                    <div>
                                        <span className="text-xl font-bold text-foreground">#{stats?.rank_agency}</span>
                                        <p className="text-xs text-muted-foreground">Agence</p>
                                    </div>
                                    <div>
                                        <span className="text-xl font-bold text-foreground">#{stats?.rank_local}</span>
                                        <p className="text-xs text-muted-foreground">Local</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs - Scrollable chips on mobile */}
            <div className="border-b border-border sticky top-0 bg-background/95 backdrop-blur-sm z-10">
                <div className="max-w-7xl mx-auto px-3 sm:px-6">
                    <nav className="flex gap-1.5 overflow-x-auto py-2 scrollbar-hide" data-testid="reputation-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => !tab.locked && setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-all whitespace-nowrap shrink-0 ${
                                    tab.locked
                                        ? 'text-muted-foreground/40 cursor-default'
                                        : activeTab === tab.id
                                            ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg shadow-[#681793]/20'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                                data-testid={`tab-${tab.id}`}
                            >
                                {tab.locked ? <Lock size={14} /> : <tab.icon size={16} />}
                                <span className="sm:hidden">{tab.shortLabel}</span>
                                <span className="hidden sm:inline">{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
                <AnimatePresence mode="wait">
                    {activeTab === 'overview' && (
                        <motion.div
                            key="overview"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="space-y-4 sm:space-y-8"
                        >
                            {/* Season Progress */}
                            <SeasonProgress seasonData={seasonData} />

                            {/* Quick Stats Grid - 2x2 on mobile */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                                <StatCard
                                    icon={Trophy}
                                    label="Ventes"
                                    value={stats?.total_closings}
                                    color="#F59E0B"
                                />
                                <StatCard
                                    icon={Star}
                                    label="Note moy."
                                    value="4.6"
                                    suffix="/5"
                                    color="#10B981"
                                />
                                <StatCard
                                    icon={Award}
                                    label="Badges"
                                    value={badges.length}
                                    color="#8B5CF6"
                                />
                                <StatCard
                                    icon={Zap}
                                    label="XP Saison"
                                    value={seasonData?.season_xp}
                                    color="#EC4899"
                                />
                            </div>

                            {/* Confidence Breakdown - Simplified on mobile */}
                            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-border/50">
                                <h3 className="text-sm sm:text-lg font-semibold text-foreground mb-4 sm:mb-6 flex items-center gap-2">
                                    <Shield size={18} className="text-blue-400" />
                                    Indice de Confiance
                                </h3>
                                <ConfidenceIndex index={stats?.confidence_index} size="lg" showBreakdown />
                            </div>

                            {/* Recent Badges - Scrollable on mobile */}
                            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-border/50">
                                <div className="flex justify-between items-center mb-4 sm:mb-6">
                                    <h3 className="text-sm sm:text-lg font-semibold text-foreground flex items-center gap-2">
                                        <Award size={18} className="text-yellow-400" />
                                        Mes Badges
                                    </h3>
                                    <button 
                                        onClick={() => setActiveTab('badges')}
                                        className="text-xs sm:text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                                    >
                                        Tout <ChevronRight size={14} />
                                    </button>
                                </div>
                                <div className="flex gap-3 overflow-x-auto scrollbar-hide sm:grid sm:grid-cols-3 md:grid-cols-6 sm:gap-4 -mx-1 px-1 pb-1">
                                    {badges.slice(0, 6).map((badge) => (
                                        <div key={badge.id} className="flex-shrink-0 w-20 sm:w-auto">
                                            <Badge badge={badge} size="md" />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Active Missions */}
                            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-border/50">
                                <div className="flex justify-between items-center mb-4 sm:mb-6">
                                    <h3 className="text-sm sm:text-lg font-semibold text-foreground flex items-center gap-2">
                                        <Target size={18} className="text-purple-400" />
                                        Missions de la semaine
                                    </h3>
                                    <button 
                                        onClick={() => setActiveTab('missions')}
                                        className="text-xs sm:text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                                    >
                                        Tout <ChevronRight size={14} />
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                    {missions.weekly.slice(0, 3).map((mission) => (
                                        <MissionCard key={mission.id} mission={mission} />
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {activeTab === 'badges' && (
                        <motion.div
                            key="badges"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <BadgesTab badges={badges} />
                        </motion.div>
                    )}

                    {activeTab === 'certifications' && (
                        <motion.div
                            key="certifications"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <CertificationsTab 
                                certifications={certifications} 
                                onStart={setActiveCertification}
                            />
                        </motion.div>
                    )}

                    {activeTab === 'missions' && (
                        <motion.div
                            key="missions"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <MissionsTab missions={missions} />
                        </motion.div>
                    )}

                    {activeTab === 'ranking' && (
                        <motion.div
                            key="ranking"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            <RankingTab stats={stats} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Certification Modal */}
            {activeCertification && (
                <CertificationModal 
                    certification={certifications.find(c => c.id === activeCertification)}
                    onClose={() => setActiveCertification(null)}
                />
            )}
        </div>
    );
};

// Stat Card Component - Compact on mobile
const StatCard = ({ icon: Icon, label, value, suffix = '', color }) => (
    <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-border/50">
        <div className="flex items-center gap-2 sm:gap-3">
            <div 
                className="p-1.5 sm:p-2 rounded-lg"
                style={{ backgroundColor: `${color}20` }}
            >
                <Icon size={16} className="sm:hidden" style={{ color }} />
                <Icon size={20} className="hidden sm:block" style={{ color }} />
            </div>
            <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold text-foreground">{value}{suffix}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{label}</p>
            </div>
        </div>
    </div>
);

// Badges Tab
const BadgesTab = ({ badges }) => {
    const categories = [
        { id: 'service', name: 'Service', icon: Zap },
        { id: 'communication', name: 'Communication', icon: BookOpen },
        { id: 'relation', name: 'Relation', icon: Users },
        { id: 'performance', name: 'Performance', icon: TrendingUp },
        { id: 'expertise', name: 'Expertise', icon: Star },
        { id: 'closing', name: 'Clôture', icon: Trophy }
    ];

    return (
        <div className="space-y-8">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-border/50">
                <h3 className="text-lg font-semibold text-foreground mb-2">Mes Badges Affichés</h3>
                <p className="text-sm text-muted-foreground mb-6">
                    Vous pouvez afficher jusqu'à 6 badges sur votre profil public. 
                    Cliquez sur un badge pour le masquer/afficher.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
                    {badges.map((badge) => (
                        <Badge key={badge.id} badge={badge} size="lg" showDetails />
                    ))}
                </div>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-6 border border-border/50">
                <h3 className="text-lg font-semibold text-foreground mb-6">Tous les Badges</h3>
                <div className="space-y-8">
                    {categories.map((cat) => (
                        <div key={cat.id}>
                            <h4 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                                <cat.icon size={16} />
                                {cat.name}
                            </h4>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {/* Badge placeholders for each category */}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Certifications Tab
const CertificationsTab = ({ certifications, onStart }) => {
    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-xl p-6 border border-purple-500/20">
                <h3 className="text-lg font-semibold text-foreground mb-2">Certifications Professionnelles</h3>
                <p className="text-sm text-muted-foreground">
                    Passez des certifications pour gagner des bonus Elo, des badges de compétence et 
                    améliorer votre visibilité. Chaque certification peut être repassée après une période de cooldown.
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {certifications.map((cert) => (
                    <CertificationCard 
                        key={cert.id}
                        certification={cert}
                        onStart={onStart}
                        isPassed={false}
                        canRetake={true}
                    />
                ))}
            </div>
        </div>
    );
};

// Missions Tab
const MissionsTab = ({ missions }) => {
    return (
        <div className="space-y-8">
            {/* Weekly Missions */}
            <div>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Calendar size={20} className="text-purple-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-foreground">Missions Hebdomadaires</h3>
                        <p className="text-sm text-muted-foreground">Réinitialisées chaque lundi</p>
                    </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    {missions.weekly.map((mission) => (
                        <MissionCard key={mission.id} mission={mission} />
                    ))}
                </div>
            </div>

            {/* Monthly Missions */}
            <div>
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 bg-pink-500/20 rounded-lg">
                        <Crown size={20} className="text-pink-400" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-foreground">Missions Mensuelles</h3>
                        <p className="text-sm text-muted-foreground">Réinitialisées le 1er du mois</p>
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    {missions.monthly.map((mission) => (
                        <MissionCard key={mission.id} mission={mission} />
                    ))}
                </div>
            </div>
        </div>
    );
};

// Ranking Tab
const RankingTab = ({ stats }) => {
    const [ranking, setRanking] = useState([]);
    const [rankingType, setRankingType] = useState('agency');
    const [period, setPeriod] = useState('season');
    const [loading, setLoading] = useState(true);

    const loadRanking = useCallback(async () => {
        try {
            setLoading(true);
            let endpoint = '';
            if (rankingType === 'agency') {
                endpoint = `/reputation/leaderboard/current-agency?period=${period}`;
            } else if (rankingType === 'local') {
                endpoint = `/reputation/leaderboard/local`;
            } else {
                endpoint = `/reputation/leaderboard/local`;
            }
            
            const response = await api.get(endpoint);
            if (response.data?.agents) {
                setRanking(response.data.agents.map((a, i) => ({
                    rank: a.rank || i + 1,
                    name: a.is_current_user ? 'Vous' : a.name,
                    elo: period === 'season' ? (a.elo_season || 1000) : (a.elo_career || 1000),
                    level: a.level_info?.level_id || 'essentiel',
                    isYou: a.is_current_user
                })));
            }
        } catch (error) {
            console.error('Error loading ranking:', error);
            setRanking([
                { rank: 1, name: 'Vous', elo: stats?.elo_season || 1000, level: stats?.level?.level_id || 'essentiel', isYou: true }
            ]);
        } finally {
            setLoading(false);
        }
    }, [rankingType, period, stats]);

    useEffect(() => {
        loadRanking();
    }, [loadRanking]);

    const levelColors = {
        essentiel: '#6B7280',
        confirme: '#3B82F6',
        expert: '#8B5CF6',
        reference: '#F59E0B',
        elite: '#EC4899'
    };

    const now = new Date();
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Ranking Type Chips */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {[
                    { value: 'agency', label: 'Agence' },
                    { value: 'local', label: 'Secteur' },
                    { value: 'national', label: 'National' }
                ].map(item => (
                    <button 
                        key={item.value}
                        onClick={() => setRankingType(item.value)}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all shrink-0 ${
                            rankingType === item.value 
                                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg' 
                                : 'bg-card text-muted-foreground hover:text-foreground border border-border'
                        }`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {/* Leaderboard */}
            <div className="bg-gray-800/50 rounded-xl border border-border/50 overflow-hidden">
                <div className="p-3 sm:p-4 border-b border-border/50">
                    <h3 className="font-semibold text-white text-sm sm:text-base">
                        Saison - {monthNames[now.getMonth()]} {now.getFullYear()}
                    </h3>
                </div>
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent mx-auto"></div>
                    </div>
                ) : ranking.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                        Aucun agent dans le classement
                    </div>
                ) : (
                    <div className="divide-y divide-gray-700/50">
                        {ranking.map((agent) => (
                            <div 
                                key={agent.rank}
                                className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 ${
                                    agent.isYou ? 'bg-purple-900/20' : 'hover:bg-gray-800/50'
                                }`}
                            >
                                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-bold text-xs sm:text-sm ${
                                    agent.rank === 1 ? 'bg-yellow-500 text-yellow-900' :
                                    agent.rank === 2 ? 'bg-gray-300 text-gray-700' :
                                    agent.rank === 3 ? 'bg-amber-700 text-amber-100' :
                                    'bg-gray-700 text-foreground'
                                }`}>
                                    {agent.rank}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${agent.isYou ? 'text-purple-300' : 'text-white'}`}>
                                        {agent.name}
                                    </p>
                                </div>
                                <div 
                                    className="px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium hidden sm:block"
                                    style={{ 
                                        backgroundColor: `${levelColors[agent.level] || levelColors.essentiel}20`,
                                        color: levelColors[agent.level] || levelColors.essentiel
                                    }}
                                >
                                    {agent.level ? agent.level.charAt(0).toUpperCase() + agent.level.slice(1) : 'Essentiel'}
                                </div>
                                <div className="flex items-center gap-1.5 w-16 sm:w-24 justify-end">
                                    <span className="font-bold text-white text-sm">{agent.elo}</span>
                                    {agent.isYou && <TrendingUp size={14} className="text-green-400" />}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Certification Modal
const CertificationModal = ({ certification, onClose }) => {
    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(certification?.duration_minutes * 60 || 300);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadQuestions = useCallback(async () => {
        try {
            const res = await api.post(`/reputation/certifications/${certification.id}/start`);
            setQuestions(res.data?.questions || []);
        } catch (error) {
            console.error('Error loading questions:', error);
        } finally {
            setLoading(false);
        }
    }, [certification.id]);

    const handleAnswer = (questionId, answerId) => {
        setAnswers(prev => ({ ...prev, [questionId]: answerId }));
    };

    const handleSubmit = useCallback(async () => {
        try {
            const res = await api.post(`/reputation/certifications/${certification.id}/submit`, {
                certification_id: certification.id,
                answers
            });
            setResult(res.data);
            setSubmitted(true);
        } catch (error) {
            console.error('Error submitting:', error);
        }
    }, [certification.id, answers]);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    useEffect(() => {
        if (timeLeft > 0 && !submitted) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        } else if (timeLeft === 0 && !submitted) {
            handleSubmit();
        }
    }, [timeLeft, submitted, handleSubmit]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div 
                className="bg-background rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
            >
                {!submitted ? (
                    <>
                        {/* Header */}
                        <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 p-6 border-b border-border">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold text-white">{certification.name}</h2>
                                    <p className="text-sm text-muted-foreground">
                                        Question {currentQuestion + 1} / {questions.length}
                                    </p>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                                        timeLeft < 60 ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-foreground'
                                    }`}>
                                        <Clock size={16} />
                                        {formatTime(timeLeft)}
                                    </div>
                                    <button onClick={onClose} className="text-muted-foreground hover:text-white">
                                        <X size={24} />
                                    </button>
                                </div>
                            </div>
                            {/* Progress bar */}
                            <div className="h-1 bg-gray-700 rounded-full mt-4 overflow-hidden">
                                <div 
                                    className="h-full bg-purple-500 transition-all duration-300"
                                    style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                                />
                            </div>
                        </div>

                        {/* Question */}
                        <div className="p-6">
                            {questions[currentQuestion] && (
                                <div>
                                    <h3 className="text-lg font-medium text-white mb-6">
                                        {questions[currentQuestion].question}
                                    </h3>
                                    <div className="space-y-3">
                                        {questions[currentQuestion].options.map((option) => (
                                            <button
                                                key={option.id}
                                                onClick={() => handleAnswer(questions[currentQuestion].id, option.id)}
                                                className={`w-full text-left p-4 rounded-xl border transition-all ${
                                                    answers[questions[currentQuestion].id] === option.id
                                                        ? 'border-purple-500 bg-purple-500/20'
                                                        : 'border-border bg-gray-800/50 hover:border-gray-600'
                                                }`}
                                            >
                                                <span className="text-foreground">{option.text}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t border-border flex justify-between">
                            <button
                                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                                disabled={currentQuestion === 0}
                                className="px-4 py-2 text-muted-foreground hover:text-white disabled:opacity-50"
                            >
                                Précédent
                            </button>
                            {currentQuestion < questions.length - 1 ? (
                                <button
                                    onClick={() => setCurrentQuestion(currentQuestion + 1)}
                                    className="px-6 py-2 bg-purple-500 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"
                                >
                                    Suivant <ArrowRight size={18} />
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg"
                                >
                                    Terminer
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    /* Results */
                    <div className="p-8 text-center">
                        <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
                            result?.passed ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                            {result?.passed ? (
                                <CheckCircle size={40} className="text-green-400" />
                            ) : (
                                <X size={40} className="text-red-400" />
                            )}
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">
                            {result?.passed ? 'Félicitations !' : 'Pas cette fois...'}
                        </h2>
                        <p className="text-muted-foreground mb-6">
                            Score: {result?.score}% ({result?.correct_answers}/{result?.total_questions})
                        </p>
                        {result?.passed && result?.rewards && (
                            <div className="flex justify-center gap-4 mb-6">
                                <div className="px-4 py-2 bg-purple-500/20 rounded-lg text-purple-300">
                                    <TrendingUp size={16} className="inline mr-2" />
                                    +{result.rewards.elo_bonus} Elo
                                </div>
                                {result.rewards.badge && (
                                    <div className="px-4 py-2 bg-yellow-500/20 rounded-lg text-yellow-300">
                                        <Award size={16} className="inline mr-2" />
                                        Badge débloqué
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            onClick={onClose}
                            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
                        >
                            Fermer
                        </button>
                    </div>
                )}
            </motion.div>
        </div>
    );
};

export default AgentReputationPage;
